import {useState, useRef, useEffect} from 'react';

type TUseIsFirstRenderOptions = 'state' | 'ref';

/**
 * Hook to determine if we are on the first render or not.
 * Depending on the method chosen ('state' or 'ref'), the behavior varies:
 * - 'state': Uses React's state to determine and can cause re-renders.
 * - 'ref': Uses React's useRef to determine without causing re-renders.
 *
 * @param {TUseIsFirstRenderOptions} method - method of checking (either 'state' or 'ref'). Default is 'ref'.
 * @returns {boolean} - true if it's the first render, false otherwise.
 */
function useIsFirstRender(method: TUseIsFirstRenderOptions = 'ref'): boolean {
	const [isFirstState, setIsFirstState] = useState(true);
	const isFirstRef = useRef(true);

	useEffect(() => {
		if (isFirstState && method === 'state') {
			setIsFirstState(false);
		}
	}, [isFirstState, method]);

	if (method === 'state') {
		return isFirstState;
	}

	if (isFirstRef.current) {
		isFirstRef.current = false;

		return true;
	}

	return false;
}

export default useIsFirstRender;
